import React, {
	useCallback, useState, useEffect
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import { injectIntl, defineMessages } from 'react-intl';

import './index.sass';
import { getCustomerFieldValue, usePrevious } from '../../../shared/utility';
import * as actions from '../../../store/actions';
import ChatHeader from '../ChatHeader';
import ChatInput from '../ChatInput';
import InternalMessages from '../../Molecules/InternalMessages';
import ChatMail from '../ChatMail';
import MessageList from '../MessageList';
import DragAndDropFileUpload from '../../Molecules/DragAndDropFileUpload';
import PhoneInteraction from '../PhoneInteraction';

const messagesIntl = defineMessages({
	previewMessage: {
		id: 'chatBox.previewMessage',
		defaultMessage: 'Esta é uma pré-visualização do atendimento, para iniciar este atendimento clique no botão de "atender" acima.'
	}
});

const ChatBox = ({
	currentInteraction,
	isHistoryInteraction,
	onFinishInteraction,
	agent,
	closeReceptiveModal,
	blockedToInteract,
	showingInternalMessages,
	intl
}) => {
	const {
		messages,
		typing,
		interactionType,
		startTime,
		createdAt,
		customerInfo,
		currentState,
		endTime,
		finishedAt,
		media,
		interactionHash
	} = currentInteraction;
	const { formatMessage } = intl;
	const showPhoneInteraction = interactionType.includes('PHONE') && !['MISSED', 'REPLYING'].includes(currentState);
	const isEnded = currentState === 'ENDED';
	const isEmail = ['offcontact', 'email'].includes(interactionType.toLowerCase());
	const prevInteractionHash = usePrevious(interactionHash);

	const [openedDragAndDrop, setOpenedDragAndDrop] = useState(false);
	const [hasFileDragged, setHasFileDragged] = useState(false);

	const finishInteraction = useCallback(() => onFinishInteraction({
		interactionHash,
		media,
		id: agent.info.account.id
	}), [agent.info.account.id, interactionHash, media, onFinishInteraction]);

	useEffect(() => {
		if (interactionHash !== prevInteractionHash) {
			setOpenedDragAndDrop(false);
		}
	}, [
		interactionHash,
		prevInteractionHash
	]);

	const debouncedDrag = debounce((status) => {
		if (!isHistoryInteraction) {
			setOpenedDragAndDrop(status);
		}
	}, 100, { leading: !openedDragAndDrop });

	const checkIfDragFileIsValid = () => {
		setTimeout(() => {
			if (!hasFileDragged) {
				setOpenedDragAndDrop(false);
			}
		}, 1000);
	};

	const handleDragOver = (event) => {
		event.preventDefault();
		const { items } = event.dataTransfer;

		if (items && items.length > 0 && items[0].kind === 'file') {
			debouncedDrag(true);
		}
	};

	return (
		<div
			onDragOver={handleDragOver}
			onDragLeave={() => debouncedDrag(false)}
			onDrop={checkIfDragFileIsValid}
			className="ChatBox"
		>
			<DragAndDropFileUpload
				open={openedDragAndDrop}
				onClose={() => setOpenedDragAndDrop(false)}
				setHasFileDragged={setHasFileDragged}
				interactionType={interactionType}
			/>
			<ChatHeader blockedToInteract={blockedToInteract} isHistoryInteraction={isHistoryInteraction} />
			<div className="ChatBox__chatInfo">
				<div className="ChatBox__chat">
					{blockedToInteract && (
						<div className="ChatBox__chat__warning">
							<p>{formatMessage(messagesIntl.previewMessage)}</p>
						</div>
					)}
					{showPhoneInteraction ? (
						<PhoneInteraction
							startTime={startTime || createdAt}
							phoneNumber={customerInfo ? getCustomerFieldValue('channel_phone', customerInfo.fields) : ''}
							isEnded={isEnded}
							endTime={endTime || finishedAt}
							finishInteraction={finishInteraction}
							interactionHash={interactionHash}
							closeReceptiveModal={closeReceptiveModal}
						/>
					) : (
						<MessageList messages={messages} typing={typing} />
					)}
				</div>
			</div>
			{blockedToInteract ? (
				<div className="ChatBox__chat__warning-input">
					<p>{formatMessage(messagesIntl.previewMessage)}</p>
				</div>
			) : (
				<>
					{isEmail
						? <ChatMail isHistoryInteraction={isHistoryInteraction} />
						: <ChatInput isHistoryInteraction={isHistoryInteraction} />
					}
				</>
			)}
			{showingInternalMessages && <InternalMessages />}
		</div>
	);
};

const mapStateToProps = state => ({
	isHistoryInteraction: state.interaction.currentInteractionHash
		? state.interaction.historyInteractions.some(interaction => (
			interaction.interactionHash === state.interaction.currentInteractionHash
		)) : false,
	agent: state.agent,
	transferData: state.interaction.transferData,
	showingInternalMessages: state.interface.showingInternalMessages
});

const mapActionsToProps = dispatch => ({
	onFinishInteraction: info => dispatch(actions.onFinish(info)),
	closeReceptiveModal: () => dispatch(actions.hideReceptiveModal())
});

ChatBox.propTypes = {
	protocolInfo: PropTypes.shape({
		department: PropTypes.string.isRequired,
		protocol: PropTypes.oneOf(['string', 'number'])
	}),
	currentInteraction: PropTypes.shape({
		messages: PropTypes.arrayOf(PropTypes.shape({})),
		message: PropTypes.shape({}),
		id: PropTypes.number,
		typing: PropTypes.bool,
		department: PropTypes.shape({
			name: PropTypes.string
		}),
		interactionType: PropTypes.string,
		startTime: PropTypes.string,
		createdAt: PropTypes.string,
		customerInfo: PropTypes.shape({
			fields: PropTypes.arrayOf(PropTypes.shape({}))
		}),
		currentState: PropTypes.string,
		endTime: PropTypes.string,
		finishedAt: PropTypes.instanceOf(Date),
		media: PropTypes.string,
		interactionHash: PropTypes.string,
		departmentId: PropTypes.number
	}),
	isHistoryInteraction: PropTypes.bool.isRequired,
	showingInternalMessages: PropTypes.bool.isRequired,
	onFinishInteraction: PropTypes.func.isRequired,
	agent: PropTypes.shape({
		info: PropTypes.shape({
			account: PropTypes.shape({
				id: PropTypes.number
			})
		})
	}).isRequired,
	intl: PropTypes.shape({
		formatMessage: PropTypes.func.isRequired
	}).isRequired,
	closeReceptiveModal: PropTypes.func.isRequired,
	blockedToInteract: PropTypes.bool
};

export default connect(mapStateToProps, mapActionsToProps)(injectIntl(ChatBox));
